import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { OnboardingApi, UnitCompletionStatus } from './onboardingApi';
import { UnitCompletionRecord } from '../types';

const MAX_COMPLETIONS_URL_LENGTH = 2000;

export interface OnboardingApiClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

/**
 * {@link OnboardingApi} implementation backed by
 * `@platt/plugin-platt-onboarding-backend`'s HTTP API.
 */
export class OnboardingApiClient implements OnboardingApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: OnboardingApiClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getUnitCompletion(unitRef: string): Promise<UnitCompletionStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('platt-onboarding');
    const url = `${baseUrl}/completion?${new URLSearchParams({ unitRef })}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as UnitCompletionStatus;
  }

  async completeUnit(unitRef: string): Promise<UnitCompletionStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('platt-onboarding');
    const response = await this.fetchApi.fetch(`${baseUrl}/completion`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ unitRef }),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as UnitCompletionStatus;
  }

  async uncompleteUnit(unitRef: string): Promise<UnitCompletionStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('platt-onboarding');
    const url = `${baseUrl}/completion?${new URLSearchParams({ unitRef })}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as UnitCompletionStatus;
  }

  async listCompletions(unitRefs: string[]): Promise<UnitCompletionRecord[]> {
    if (unitRefs.length === 0) {
      return [];
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('platt-onboarding');
    const prefix = `${baseUrl}/completions?`;
    const urls: string[] = [];
    let query = '';
    for (const unitRef of new Set(unitRefs)) {
      const param = new URLSearchParams({ unitRef }).toString();
      if (prefix.length + param.length > MAX_COMPLETIONS_URL_LENGTH) {
        throw new Error(
          'Unit reference exceeds the completion request URL limit',
        );
      }
      if (
        query &&
        prefix.length + query.length + 1 + param.length >
          MAX_COMPLETIONS_URL_LENGTH
      ) {
        urls.push(`${prefix}${query}`);
        query = '';
      }
      query += `${query ? '&' : ''}${param}`;
    }
    urls.push(`${prefix}${query}`);

    const completions: UnitCompletionRecord[] = [];
    for (const url of urls) {
      const response = await this.fetchApi.fetch(url);
      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }
      const body = (await response.json()) as {
        completions: UnitCompletionRecord[];
      };
      completions.push(...body.completions);
    }
    return completions;
  }
}
